import { useCallback, useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']"
].join(",");

function getFocusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => {
      if (element.hasAttribute("disabled")) return false;
      if (element.getAttribute("aria-hidden") === "true") return false;
      if (element.offsetParent === null && getComputedStyle(element).position !== "fixed") return false;
      return true;
    });
}

function isTextEditingElement(target: HTMLElement | null) {
  if (!target) return false;

  if (target.closest(".ant-picker-dropdown")) return true;
  if (target.closest(".ant-select-dropdown")) return true;

  const tag = target.tagName.toLowerCase();
  if (tag === "textarea" || tag === "select") return true;

  if (tag === "input") {
    const input = target as HTMLInputElement;
    const type = (input.type || "text").toLowerCase();
    return !["checkbox", "radio", "button", "submit", "reset", "range"].includes(type);
  }

  return target.isContentEditable;
}

function isPlainTextInput(target: HTMLElement | null) {
  if (!target) return false;

  const tag = target.tagName.toLowerCase();
  if (tag === "textarea") return true;

  if (tag === "input") {
    const input = target as HTMLInputElement;
    const type = (input.type || "text").toLowerCase();
    return !["checkbox", "radio", "button", "submit", "reset", "range"].includes(type);
  }

  return target.isContentEditable;
}

function isNumericInput(target: HTMLElement | null) {
  if (!target) return false;

  if (target.closest(".ant-input-number")) return true;

  if (target.tagName.toLowerCase() === "input") {
    const input = target as HTMLInputElement;
    return (input.type || "").toLowerCase() === "number";
  }

  return false;
}

function getDropdownControl(target: HTMLElement | null) {
  return target?.closest(
    ".ant-select, .ant-picker, .ant-cascader-picker, .ant-mentions, .ant-auto-complete"
  ) as HTMLElement | null;
}

function isDropdownOpen(control: HTMLElement | null) {
  if (!control) return false;

  if (control.classList.contains("ant-select-open")) return true;

  const expandedTarget =
    control.matches("[aria-expanded]")
      ? control
      : control.querySelector<HTMLElement>("[aria-expanded]");

  return expandedTarget?.getAttribute("aria-expanded") === "true";
}

/**
 * Shared overlay keyboard lock.
 * Future reference:
 * 1. Trap `Tab` within the active modal/drawer.
 * 2. Keep accidental focus escapes from leaving the overlay.
 * 3. Support arrow-key focus travel for mouseless users.
 * 4. Left/Right move across controls; Up/Down are reserved for opening or navigating dropdown-style controls.
 */
export function useOverlayKeyboardLock(isOpen: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const focusFirstElement = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const focusables = getFocusableElements(container);
    const preferred = focusables.find((element) =>
      element.matches("input, textarea, select, button, [tabindex]")
    );

    (preferred ?? container).focus();
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    restoreFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const timer = window.setTimeout(() => focusFirstElement(), 0);
    return () => window.clearTimeout(timer);
  }, [focusFirstElement, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleFocusIn = (event: FocusEvent) => {
      const container = containerRef.current;
      const target = event.target instanceof HTMLElement ? event.target : null;

      if (!container) return;
      if (target && container.contains(target)) return;

      // Don't steal focus if it landed inside another active overlay
      // (e.g. a Modal opened on top of this Drawer). Any element that has
      // a [data-overlay-keyboard-lock] ancestor — or is inside an Ant
      // Design modal wrapper — counts as a separate overlay.
      if (target?.closest('[data-overlay-keyboard-lock]')) return;
      if (target?.closest('.ant-modal-wrap, .ant-modal-root')) return;

      focusFirstElement();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const container = containerRef.current;
      const target = event.target instanceof HTMLElement ? event.target : null;

      if (!container) return;

      const focusables = getFocusableElements(container);
      if (focusables.length === 0) return;

      const activeElement = target && container.contains(target) ? target : document.activeElement as HTMLElement | null;
      const currentIndex = activeElement ? focusables.indexOf(activeElement) : -1;

      if (event.key === "Tab") {
        event.preventDefault();

        const nextIndex = event.shiftKey
          ? (currentIndex <= 0 ? focusables.length - 1 : currentIndex - 1)
          : (currentIndex >= focusables.length - 1 ? 0 : currentIndex + 1);

        focusables[nextIndex]?.focus();
        return;
      }

      if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
        return;
      }

      const dropdownControl = getDropdownControl(activeElement);
      const dropdownOpen = isDropdownOpen(dropdownControl);
      const isHorizontalArrow = event.key === "ArrowLeft" || event.key === "ArrowRight";
      const isVerticalArrow = event.key === "ArrowUp" || event.key === "ArrowDown";

      if (dropdownControl) {
        // Keep native Up/Down behavior for dropdown-style controls so only
        // vertical arrows open or navigate the popup. Left/Right still move
        // focus across sibling controls when the popup is closed.
        if (dropdownOpen) {
          return;
        }

        if (isVerticalArrow) {
          return;
        }
      }

      const shouldMoveAcrossClosedDropdown =
        isHorizontalArrow &&
        dropdownControl &&
        !dropdownOpen;
      const shouldMoveAcrossNumericInput =
        isHorizontalArrow &&
        isNumericInput(activeElement);
      const shouldMoveAcrossSingleLineInput =
        isHorizontalArrow &&
        isPlainTextInput(activeElement) &&
        activeElement?.tagName.toLowerCase() !== "textarea";

      if (isTextEditingElement(activeElement) && !shouldMoveAcrossClosedDropdown && !shouldMoveAcrossNumericInput && !shouldMoveAcrossSingleLineInput) {
        return;
      }

      if (isPlainTextInput(activeElement) && !shouldMoveAcrossClosedDropdown && !shouldMoveAcrossNumericInput && !shouldMoveAcrossSingleLineInput) {
        return;
      }

      event.preventDefault();

      const moveBackward = event.key === "ArrowUp" || event.key === "ArrowLeft";
      const nextIndex = moveBackward
        ? (currentIndex <= 0 ? focusables.length - 1 : currentIndex - 1)
        : (currentIndex >= focusables.length - 1 ? 0 : currentIndex + 1);

      focusables[nextIndex]?.focus();
    };

    document.addEventListener("focusin", handleFocusIn, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("focusin", handleFocusIn, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [focusFirstElement, isOpen]);

  useEffect(() => {
    if (isOpen) return;

    const previous = restoreFocusRef.current;
    if (!previous) return;

    window.setTimeout(() => previous.focus(), 0);
    restoreFocusRef.current = null;
  }, [isOpen]);

  return containerRef;
}
